import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { IRecordedDeed } from '../classes/RecordedDeed';
import { IRecordedMortgage } from '../classes/RecordedMortgage';
import IRouterParams from '../IRouterParams';
import { RootStore } from '../services/RootStore';
import getText from '../utils/getText';
import PolicyScheduleA from './PolicyScheduleA';
import getConfig from '../utils/getConfig';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { eSingleFields } from '../context/state/eSingleFields';
import { ModalHandler } from '../context/components/ModalHandler';
import { AmericanDigitalShortFormContainer } from '../components/AmericanDigital/AmericanDigitalShortFormContainer';
import { PageLoading } from 'ui-toolkit';
import { clientUrls } from '../../ui-toolkit/rest/ClientUrlFactory';

interface IContainerPolicyScheduleAProps extends RouteComponentProps<IRouterParams> {
  rootStore: RootStore;
}

interface IContainerPolicyScheduleAState {
  showCompleteJacketDialog: boolean;
}

class ContainerPolicyScheduleA extends React.Component<IContainerPolicyScheduleAProps, IContainerPolicyScheduleAState> {
  public readonly state: IContainerPolicyScheduleAState = {
    showCompleteJacketDialog: false,
  };

  private policyType = this.props.match!.params.policyType;
  private orderId = this.props.match!.params.orderId;
  private action = this.props.match!.params.action;

  constructor(props: IContainerPolicyScheduleAProps) {
    super(props);
    this.initPolicyScheduleAData();
  }

  public render() {
    if (
      this.props.rootStore.policyScheduleAService.getUnderwriter() &&
      this.props.rootStore.policyScheduleAService.isAmericanDigital &&
      this.policyType === PolicyTypeEnum.shortForm
    ) {
      const isLoading = !!this.props.rootStore.policyScheduleAService.getloadPage.fetching;
      return (
        <>
          <ModalHandler savePage={this.savePolicy} loadPage={this.initPolicyScheduleAData} />
          <PageLoading isLoading={isLoading} />
          {isLoading ? null : <AmericanDigitalShortFormContainer rootStore={this.props.rootStore} />}
        </>
      );
    }
    return (
      <>
        <ModalHandler savePage={this.savePolicy} loadPage={this.initPolicyScheduleAData} />
        <PolicyScheduleA
          history={this.props.history}
          location={this.props.location}
          match={this.props.match}
          policyType={this.policyType}
          orderId={this.orderId}
          action={this.action}
          title={getText(`policy.${this.action}.${this.policyType}`)}
          showEndorsements={getConfig(`config.policy.showEndorsements.${this.policyType}`)}
          editAmtOfInsuredFieldName={
            this.policyType === PolicyTypeEnum.owner ? eSingleFields.purchase_price : eSingleFields.loan_amount
          }
          showMortgageeClause={getConfig(`config.policy.mortgageeClause.${this.policyType}`)}
          cdBuilderPageURL={clientUrls.urlFor.feesTab(this.orderId)}
          editAddressUrl={clientUrls.urlFor.orderEditAddress(this.orderId)}
          editBorrowerPage={
            this.policyType === PolicyTypeEnum.longForm
              ? clientUrls.urlFor.lendersIndex()
              : clientUrls.urlFor.orderStatus(this.orderId)
          }
          errorMessage={this.props.rootStore.policyScheduleAService.getPageError}
          getEndorsements={this.props.rootStore.policyScheduleAService.getEndorsements}
          getErrorRecordedMortgage={this.props.rootStore.policyScheduleAService.getErrorRecordedMortgage}
          getOrderDetails={this.props.rootStore.policyScheduleAService.getOrderDetails}
          getPolicyJacket={this.props.rootStore.policyScheduleAService.getPolicyJacket}
          getRecordedDeed={this.props.rootStore.policyScheduleAService.getRecordedDeeds}
          recordedDeedRequired={this.props.rootStore.policyScheduleAService.getRecordedDeedRequired}
          recordedMortgageRequired={this.props.rootStore.policyScheduleAService.getRecordedMortgageRequired}
          getRecordedMortgage={this.props.rootStore.policyScheduleAService.getRecordedMortgage}
          getUserRecordedDeed={this.props.rootStore.policyScheduleAService.getUserRecordedDeeds}
          getUserRecordedMortgage={this.props.rootStore.policyScheduleAService.getUserRecordedMortgage}
          onCancelRecordedMortgage={this.onCancelRecordedMortgage}
          onUpdateRecordedMortgage={this.onUpdateRecordedMortgage}
          goOtherRoute={this.nextPath}
          loadingPage={this.props.rootStore.policyScheduleAService.getloadPage.fetching}
          loadedPage={this.props.rootStore.policyScheduleAService.getloadPage.success}
          onCancelRecordedDeed={this.onCancelRecordedDeed}
          onDeletePolicyJacket={this.deletePolicy}
          onUpdatePage={this.onUpdatePage}
          policyJacketError={this.props.rootStore.policyScheduleAService.getPolicyJacketError}
          policyFetchedSuccess={
            this.props.rootStore.policyScheduleAService.getPolicyJacketFetch.success ||
            this.props.rootStore.policyScheduleAService.getPolicyNumberFetch.success
          }
          policyNumberRequired={getConfig(`config.policy.policyNumberRequired.${this.policyType}`)}
          recordedDeedWasChangedWithoutError={
            this.props.rootStore.policyScheduleAService.getRecordedDeedWasChangedWithoutError
          }
          recordedMortgageWasChangedWithoutError={
            this.props.rootStore.policyScheduleAService.getRecordedMortgageWasChangedWithoutError
          }
          updateInProcess={this.props.rootStore.policyScheduleAService.getSaveFetch.fetching}
          getErrorRecordedDeed={this.props.rootStore.policyScheduleAService.getErrorRecordedDeed()}
          onChangePolicyNumber={this.onChangePolicyNumber}
          onPolicyNumberBlur={this.savePolicy}
          onJacketUpload={this.onJacketUpload}
          updatePageInProcess={this.props.rootStore.policyScheduleAService.getUpdatePageFetch.fetching}
          updatedPage={this.props.rootStore.policyScheduleAService.getUpdatePageFetch.success}
          onUpdateRecordedDeed={this.onUpdateRecordedDeed}
          underwriter={this.props.rootStore.policyScheduleAService.getUnderwriter()}
          savePolicy={this.savePolicy}
          setUserRecordedDeed={this.setUserRecordedDeed}
          setUserRecordedMortgage={this.setUserRecordedMortgage}
          saveSuccess={this.props.rootStore.policyScheduleAService.getSaveFetch.success}
          disableNextButton={!this.props.rootStore.policyScheduleAService.scheduleAIsValid}
          isAmericanDigital={this.props.rootStore.policyScheduleAService.isAmericanDigital}
        />
        <PageLoading
          isLoading={
            !!this.props.rootStore.policyScheduleAService.getloadPage.fetching ||
            !!this.props.rootStore.policyScheduleAService.getSaveFetch.fetching ||
            !!this.props.rootStore.policyScheduleAService.getPolicyJacketFetch.fetching ||
            !!this.props.rootStore.policyScheduleAService.getPolicyNumberFetch.fetching
          }
        />
      </>
    );
  }

  private deletePolicy = () => {
    this.props.rootStore.policyScheduleAService.onDeletePolicyJacket(this.policyType, this.orderId);
  };

  private setUserRecordedDeed = (recordedDeed: IRecordedDeed, propName: string, error?: string | null) => {
    this.props.rootStore.policyScheduleAService.setUserRecordedDeeds(recordedDeed, propName, error);
  };

  private onJacketUpload = async (files: FileList | null) => {
    await this.props.rootStore.policyScheduleAService.onPolicyJacketUpload(files, this.orderId);
  };

  private nextPath = (path: string) => {
    this.props.rootStore.policyScheduleAService.policyJacketFetchSetDefault();
    this.props.history.push({ pathname: path });
  };

  private onUpdatePage = async () => {
    await this.props.rootStore.policyScheduleAService.updatePage(this.policyType, this.orderId);
  };

  private savePolicy = async () => {
    await this.props.rootStore.policyScheduleAService.savePolicy(this.orderId);
  };

  private onChangePolicyNumber = async (newValue: string | null, error?: string | null) => {
    await this.props.rootStore.policyScheduleAService.setPolicyNumber(newValue, this.policyType, error);
  };

  private onCancelRecordedDeed = () => {
    this.props.rootStore.policyScheduleAService.onCancelUserRecordedDeed();
  };

  private onCancelRecordedMortgage = () => {
    this.props.rootStore.policyScheduleAService.onCancelUserRecordedMortgage();
  };

  private onUpdateRecordedMortgage = () => {
    this.props.rootStore.policyScheduleAService.updateRecordedMortgageData(this.orderId);
  };

  private setUserRecordedMortgage = (recordedMortgage: IRecordedMortgage, propName: string, error?: string | null) => {
    this.props.rootStore.policyScheduleAService.setUserRecordedMortgage(recordedMortgage, propName, error);
  };

  private onUpdateRecordedDeed = async () => {
    this.props.rootStore.policyScheduleAService.updateRecordedDeedData(this.orderId);
  };

  private initPolicyScheduleAData = async () => {
    await this.props.rootStore.policyScheduleAService.initPolicyScheduleAData(this.policyType, this.orderId);
  };
}

export default inject('rootStore')(observer(ContainerPolicyScheduleA));
